/**
 * Series Detail Page
 *
 * Displays a series with its moves visualized in a React Flow canvas.
 *
 * Features:
 * - View mode: Read-only display of series info and move flow
 * - Edit mode: Update series, manage connections, add/remove moves
 * - React Flow canvas: Interactive node diagram with directional connections
 * - Tags management: Add/remove tags to categorize series
 * - Responsive: Desktop (left-right layout) / Mobile (top-bottom layout)
 * - Navigation: Click nodes to view move details
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, X, Maximize2, Minimize2 } from 'lucide-react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  Connection,
  applyNodeChanges,
  applyEdgeChanges,
  OnNodesChange,
  OnEdgesChange,
  OnNodeDrag,
  Panel,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { MoveNode } from '@/components/series/MoveNode'
import { AddItemsDrawer } from '@/components/shared/AddItemsDrawer'
import { MoveNodeTooltip } from '@/components/series/MoveNodeTooltip'
import { ConfirmationModal } from '@/components/shared/ConfirmationModal'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { layoutNodes, getLayoutDirection } from '@/utils/layoutNodes'
import { fetchMove } from '@/api/moves'
import { useSeriesDetail } from '@/hooks/useSeriesDetail'
import { useUpdateSeries } from '@/hooks/useUpdateSeries'
import { useDeleteSeries } from '@/hooks/useDeleteSeries'
import { useAddMovesToSeries } from '@/hooks/useAddMovesToSeries'
import { useRemoveMoveFromSeries } from '@/hooks/useRemoveMoveFromSeries'
import { useUpdateSeriesPositions } from '@/hooks/useUpdateSeriesPositions'
import { useMoves } from '@/hooks/useMoves'
import { useTagsList } from '@/hooks/useTagsList'
import {
  useSeriesConnections,
  useCreateConnection,
  useRemoveConnection,
} from '@/hooks/useMoveConnections'
import { useAddTagsToEntity, useRemoveTagFromEntity } from '@/hooks/useTaggings'
import type {
  SeriesWithRelations,
  SeriesMoveWithRelations,
  TaggingWithTag,
  MoveWithRelations,
} from '@/types/api-extensions'
import type { SeriesMovePositionUpdateDto } from '@/api/series'

// Register custom node types
const nodeTypes = {
  moveNode: MoveNode,
}

export function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Series data from API
  const {
    data: seriesData,
    isLoading: isSeriesLoading,
    isError: isSeriesError,
    error: seriesError,
    refetch: refetchSeries,
  } = useSeriesDetail(id || '', true)
  const series = seriesData as SeriesWithRelations | undefined

  // Connections data from API
  const {
    data: connections,
    isLoading: isConnectionsLoading,
    isError: isConnectionsError,
  } = useSeriesConnections(id || '', true)

  // Extract tags from series data (includeRelations=true returns taggings with nested tag objects)
  const seriesTags =
    series?.taggings?.map((t: TaggingWithTag) => t.tag).filter(Boolean) || []

  // Mutations
  const updateSeriesMutation = useUpdateSeries()
  const deleteSeriesMutation = useDeleteSeries()
  const addMovesToSeriesMutation = useAddMovesToSeries()
  const removeMoveFromSeriesMutation = useRemoveMoveFromSeries()
  const createConnectionMutation = useCreateConnection()
  const removeConnectionMutation = useRemoveConnection()
  const addTagsMutation = useAddTagsToEntity()
  const removeTagMutation = useRemoveTagFromEntity()
  const updatePositionsMutation = useUpdateSeriesPositions()

  // Track pending position updates (node id -> position)
  const pendingPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map()
  )

  // Debounced callback to persist positions to API
  const savePositions = useDebouncedCallback(() => {
    if (!id || pendingPositionsRef.current.size === 0) return

    const positions: SeriesMovePositionUpdateDto[] = Array.from(
      pendingPositionsRef.current.entries()
    ).map(([seriesMoveId, pos]) => ({
      seriesMoveId,
      positionX: pos.x,
      positionY: pos.y,
    }))

    updatePositionsMutation.mutate(
      { seriesId: id, data: { positions } },
      {
        onSuccess: () => {
          // Clear pending positions after successful save
          pendingPositionsRef.current.clear()
        },
      }
    )
  }, 500) // 500ms debounce

  // Available moves for drawer
  const { data: allMovesData } = useMoves({ limit: 100 })
  const allMoves = allMovesData?.moves || []

  // Available tags for drawer
  const { data: allTagsData } = useTagsList({ limit: 100 })
  const allTags = allTagsData?.tags || []

  // UI state
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showDeleteConnectionConfirm, setShowDeleteConnectionConfirm] =
    useState(false)
  const [connectionToDelete, setConnectionToDelete] = useState<{
    edgeId: string
    fromMoveId: string
    toMoveId: string
  } | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Form data (for edit mode) - initialize from API data
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // React Flow state
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false)

  // Tooltip state
  const [tooltipMove, setTooltipMove] = useState<{
    id: string
    name: string
    description: string
    url: string | null
    thumbnailUrl?: string | null
    taggings?: TaggingWithTag[]
    position: { x: number; y: number }
  } | null>(null)

  // Track window width for responsive layout
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const showEditModeRequiredToast = useCallback(() => {
    toast({
      title: 'Edit mode required',
      description: 'Switch to Edit mode before editing the series flow.',
      variant: 'destructive',
    })
  }, [toast])

  // Initialize form data when series loads
  useEffect(() => {
    if (series) {
      setName(series.name)
      setDescription(series.description)
    }
  }, [series])

  /**
   * Shared helper to open the move tooltip for a given move
   */
  const openMoveTooltip = useCallback(
    async (moveId: string, position: { x: number; y: number }) => {
      // Only show tooltip in read mode to avoid conflicts with dragging
      if (isEditMode) return

      // Find the full move data from series moves
      const seriesMove = series?.seriesMoves?.find(
        (sm: SeriesMoveWithRelations) => sm.move?.id === moveId
      )

      if (!seriesMove?.move) return

      // First show tooltip with basic data
      setTooltipMove({
        id: seriesMove.move.id,
        name: seriesMove.move.name,
        description: seriesMove.move.description,
        url: seriesMove.move.url ?? null,
        thumbnailUrl: seriesMove.move.thumbnailUrl ?? null,
        taggings: (seriesMove.move as MoveWithRelations).taggings || [],
        position,
      })

      // Fetch full move details with taggings if not already present
      if (!(seriesMove.move as MoveWithRelations).taggings) {
        try {
          const fullMove = await fetchMove(seriesMove.move.id, true)

          // Update tooltip with taggings
          setTooltipMove(prev =>
            prev?.id === fullMove.id
              ? {
                  ...prev,
                  taggings: (fullMove as MoveWithRelations).taggings || [],
                }
              : prev
          )
        } catch (error) {
          console.error('Failed to fetch move taggings:', error)
          // Tooltip still shows without tags
        }
      }
    },
    [isEditMode, series]
  )

  // Initialize React Flow nodes and edges from series data
  useEffect(() => {
    if (!series || !series.seriesMoves || series.seriesMoves.length === 0) {
      setNodes([])
      setEdges([])
      return
    }

    // Create nodes from series moves
    const nodeData: Node[] = series.seriesMoves
      .filter((sm: SeriesMoveWithRelations) => sm.move)
      .map((sm: SeriesMoveWithRelations) => ({
        id: sm.id,
        type: 'moveNode',
        // Use persisted position if available, otherwise set to origin (will be laid out below)
        position:
          sm.positionX != null && sm.positionY != null
            ? { x: sm.positionX, y: sm.positionY }
            : { x: 0, y: 0 },
        data: {
          moveId: sm.move!.id,
          name: sm.move!.name,
          description: sm.move!.description,
          thumbnailUrl: sm.move!.thumbnailUrl,
          onClick: (event?: React.MouseEvent) => {
            const position = event
              ? { x: event.clientX, y: event.clientY }
              : {
                  x: window.innerWidth / 2,
                  y: window.innerHeight / 2,
                }

            void openMoveTooltip(sm.move!.id, position)
          },
        },
      }))

    // Create edges from connections
    const edgeData: Edge[] = []
    if (connections && connections.length > 0 && series.seriesMoves) {
      connections.forEach(conn => {
        const sourceSeriesMove = series.seriesMoves?.find(
          (sm: SeriesMoveWithRelations) => sm.move?.id === conn.fromMoveId
        )
        const targetSeriesMove = series.seriesMoves?.find(
          (sm: SeriesMoveWithRelations) => sm.move?.id === conn.toMoveId
        )

        if (sourceSeriesMove && targetSeriesMove) {
          edgeData.push({
            id: conn.id,
            source: sourceSeriesMove.id,
            target: targetSeriesMove.id,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#000000', strokeWidth: 2 },
            markerEnd: { type: 'arrowclosed', color: '#000000' },
          })
        }
      })
    }

    setEdges(edgeData)

    setNodes(prevNodes => {
      const prevIds = new Set(prevNodes.map(node => node.id))
      const nextIds = new Set(nodeData.map(node => node.id))
      const hasNodeChanges =
        prevNodes.length === 0 ||
        prevIds.size !== nextIds.size ||
        Array.from(nextIds).some(id => !prevIds.has(id))

      if (hasNodeChanges) {
        // Only auto-layout nodes that don't have saved positions
        const nodesToLayout = nodeData.filter(
          node => node.position.x === 0 && node.position.y === 0
        )
        const nodesWithPositions = nodeData.filter(
          node => node.position.x !== 0 || node.position.y !== 0
        )

        if (nodesToLayout.length > 0) {
          const direction = getLayoutDirection(window.innerWidth)
          const layoutedNodes = layoutNodes(nodesToLayout, edgeData, {
            direction,
          })
          // Merge layouted nodes with nodes that already have positions
          return [...nodesWithPositions, ...layoutedNodes]
        }

        return nodeData
      }

      const nodeMap = new Map(nodeData.map(node => [node.id, node]))
      return prevNodes.map(node => {
        const updated = nodeMap.get(node.id)
        if (!updated) {
          return node
        }

        const positionChanged =
          updated.position.x !== node.position.x ||
          updated.position.y !== node.position.y
        const dataChanged = updated.data !== node.data

        if (!positionChanged && !dataChanged) {
          return node
        }

        return {
          ...node,
          data: dataChanged ? updated.data : node.data,
          position: positionChanged ? { ...updated.position } : node.position,
        }
      })
    })
  }, [series, connections, openMoveTooltip])

  /**
   * Re-layout nodes (e.g., after window resize or adding nodes)
   */
  const relayout = useCallback(() => {
    const direction = getLayoutDirection(window.innerWidth)
    const layoutedNodes = layoutNodes(nodes, edges, { direction })
    setNodes(layoutedNodes)
  }, [nodes, edges])

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth
      const previousWidth = windowWidth
      setWindowWidth(newWidth)

      // Re-layout if direction changes (mobile ↔ desktop)
      if (
        (previousWidth < 768 && newWidth >= 768) ||
        (previousWidth >= 768 && newWidth < 768)
      ) {
        relayout()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [windowWidth, nodes, edges, relayout])

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    if (isFullscreen) {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [isFullscreen])

  /**
   * Toggle edit mode
   */
  const handleEditClick = () => {
    setIsEditMode(true)
  }

  /**
   * Cancel edit mode
   */
  const handleCancelEdit = () => {
    if (!series) return

    // Check for unsaved changes
    const hasChanges =
      name !== series.name || description !== series.description

    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      )
      if (!confirmed) return
    }

    // Reset form
    setName(series.name)
    setDescription(series.description)
    setIsEditMode(false)
  }

  /**
   * Save series changes
   */
  const handleSave = async () => {
    if (!id || !series) return

    // Client-side validation
    const trimmedName = name.trim()
    const trimmedDescription = description.trim()

    if (!trimmedName || trimmedName.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Name is required',
        variant: 'destructive',
      })
      return
    }

    if (trimmedName.length > 255) {
      toast({
        title: 'Validation Error',
        description: 'Name cannot exceed 255 characters',
        variant: 'destructive',
      })
      return
    }

    updateSeriesMutation.mutate(
      {
        seriesId: id,
        data: {
          name: trimmedName,
          description: trimmedDescription,
        },
      },
      {
        onSuccess: () => {
          setIsEditMode(false)
        },
        onError: error => {
          console.error('Failed to update series:', error)
        },
      }
    )
  }

  /**
   * Handle delete button click
   */
  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  /**
   * Confirm delete series
   */
  const handleConfirmDelete = () => {
    if (!id) return

    deleteSeriesMutation.mutate(id, {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Series deleted successfully',
        })
        navigate('/series')
      },
      onError: error => {
        console.error('Failed to delete series:', error)
        toast({
          title: 'Error',
          description: 'Failed to delete series. Please try again.',
          variant: 'destructive',
        })
        setShowDeleteConfirm(false)
      },
    })
  }

  /**
   * Handle adding moves to the series via drawer (batch operation)
   */
  const handleAddMoves = useCallback(
    async (moveIds: string[]) => {
      if (!id || moveIds.length === 0) return

      try {
        // Add moves one by one (API supports single move addition)
        for (const moveId of moveIds) {
          await addMovesToSeriesMutation.mutateAsync({ seriesId: id, moveId })
        }
        setIsDrawerOpen(false)
      } catch {
        // Error handling is done in the mutation hook
      }
    },
    [id, addMovesToSeriesMutation]
  )

  /**
   * Handle removing a move from the series
   */
  const handleRemoveMove = useCallback(
    async (moveId: string) => {
      if (!id || !series) return

      // Find the series-move relationship ID
      const seriesMove = series?.seriesMoves?.find(
        (sm: SeriesMoveWithRelations) => sm.move?.id === moveId
      )

      if (!seriesMove) {
        toast({
          title: 'Error',
          description: 'Could not find move in series',
          variant: 'destructive',
        })
        return
      }

      try {
        await removeMoveFromSeriesMutation.mutateAsync({
          seriesMoveId: seriesMove.id,
          seriesId: id,
        })
        setTooltipMove(null)
      } catch (error) {
        // Error handling is done in the mutation hook
      }
    },
    [id, series, removeMoveFromSeriesMutation, toast]
  )

  /**
   * Handle adding tags to the series
   */
  const handleAddTags = async (tagIds: string[]) => {
    if (!series) return

    try {
      await addTagsMutation.mutateAsync({
        entityType: 'series',
        entityId: series.id,
        data: { tagIds },
      })
      setIsTagDrawerOpen(false)
      // Refetch series data to get updated taggings
      await refetchSeries()
      toast({
        title: 'Tags added',
        description: 'Tags have been added to this series successfully.',
      })
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  /**
   * Handle removing a tag from the series
   */
  const handleRemoveTag = useCallback(
    async (tagId: string) => {
      if (!id) return

      try {
        await removeTagMutation.mutateAsync({
          entityType: 'series',
          entityId: id,
          data: { tagId },
        })
        // Refetch series data to get updated taggings
        await refetchSeries()
      } catch (error) {
        // Error handling is in the mutation hook
      }
    },
    [id, removeTagMutation, refetchSeries]
  )

  /**
   * Handle connection creation (drag from source to target handle)
   */
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!id) return
      if (!connection.source || !connection.target) return

      if (!isEditMode) {
        showEditModeRequiredToast()
        return
      }

      const sourceMove = series?.seriesMoves?.find(
        (sm: SeriesMoveWithRelations) => sm.id === connection.source
      )?.move
      const targetMove = series?.seriesMoves?.find(
        (sm: SeriesMoveWithRelations) => sm.id === connection.target
      )?.move

      if (!sourceMove?.id || !targetMove?.id) {
        toast({
          title: 'Connection unavailable',
          description: 'Could not find the selected moves for this connection.',
          variant: 'destructive',
        })
        return
      }

      // Prevent self-connections
      if (sourceMove.id === targetMove.id) {
        toast({
          title: 'Invalid Connection',
          description: 'Cannot connect a move to itself',
          variant: 'destructive',
        })
        return
      }

      // Create connection via API
      createConnectionMutation.mutate(
        {
          seriesId: id,
          fromMoveId: sourceMove.id,
          toMoveId: targetMove.id,
        },
        {
          onError: error => {
            console.error('Failed to create connection:', error)
            toast({
              title: 'Error',
              description: 'Failed to create connection. Please try again.',
              variant: 'destructive',
            })
          },
        }
      )
    },
    [
      isEditMode,
      id,
      series?.seriesMoves,
      createConnectionMutation,
      showEditModeRequiredToast,
      toast,
    ]
  )

  /**
   * Handle edge click (for deletion in edit mode)
   */
  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.stopPropagation()

      if (!id) return

      if (!isEditMode) {
        showEditModeRequiredToast()
        return
      }

      // Find the connection to get fromMoveId and toMoveId
      const connection = connections?.find(conn => conn.id === edge.id)
      if (!connection) {
        toast({
          title: 'Error',
          description: 'Connection not found',
          variant: 'destructive',
        })
        return
      }

      // Store connection info and show confirmation modal
      setConnectionToDelete({
        edgeId: edge.id,
        fromMoveId: connection.fromMoveId,
        toMoveId: connection.toMoveId,
      })
      setShowDeleteConnectionConfirm(true)
    },
    [isEditMode, id, connections, showEditModeRequiredToast, toast]
  )

  /**
   * Handle node click to show tooltip with move details
   */
  const onNodeClick = useCallback(
    async (event: React.MouseEvent, node: Node) => {
      const moveId =
        (node.data as { moveId?: string } | undefined)?.moveId ?? node.id

      const position = {
        x: event.clientX,
        y: event.clientY,
      }

      await openMoveTooltip(moveId, position)
    },
    [openMoveTooltip]
  )

  /**
   * Confirm connection deletion
   */
  const handleConfirmDeleteConnection = useCallback(() => {
    if (!id || !connectionToDelete) return

    // Remove connection via API
    removeConnectionMutation.mutate(
      {
        seriesId: id,
        fromMoveId: connectionToDelete.fromMoveId,
        toMoveId: connectionToDelete.toMoveId,
      },
      {
        onSuccess: () => {
          setShowDeleteConnectionConfirm(false)
          setConnectionToDelete(null)
        },
        onError: error => {
          console.error('Failed to remove connection:', error)
          setShowDeleteConnectionConfirm(false)
          setConnectionToDelete(null)
        },
      }
    )
  }, [id, connectionToDelete, removeConnectionMutation])

  /**
   * Handle node changes (drag, delete, etc.)
   */
  const onNodesChange: OnNodesChange = useCallback(
    changes =>
      setNodes(prevNodes => {
        const updatedNodes = applyNodeChanges(changes, prevNodes)

        const positionChanges = changes.filter(
          (
            change
          ): change is Extract<
            typeof change,
            { type: 'position'; id: string }
          > => change.type === 'position' && 'id' in change
        )

        if (!isEditMode) {
          if (positionChanges.length > 0) {
            showEditModeRequiredToast()
          }
          return prevNodes
        }

        if (positionChanges.length > 0) {
          positionChanges.forEach(change => {
            const updatedNode = updatedNodes.find(node => node.id === change.id)
            if (updatedNode) {
              pendingPositionsRef.current.set(updatedNode.id, {
                x: updatedNode.position.x,
                y: updatedNode.position.y,
              })
            }
          })

          // React Flow sometimes omits or keeps the dragging flag set on
          // the final change event, so we treat any position change in edit
          // mode as a signal to persist after the debounce window.
          savePositions()
        }

        return updatedNodes
      }),
    [isEditMode, savePositions, showEditModeRequiredToast]
  )

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, node) => {
      if (!isEditMode) return

      pendingPositionsRef.current.set(node.id, {
        x: node.position.x,
        y: node.position.y,
      })

      savePositions()
    },
    [isEditMode, savePositions]
  )

  /**
   * Handle edge changes
   */
  const onEdgesChange: OnEdgesChange = useCallback(
    changes => setEdges(eds => applyEdgeChanges(changes, eds)),
    []
  )

  // Loading state
  if (isSeriesLoading || isConnectionsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <LoadingSpinner />
      </div>
    )
  }

  // Error state
  if (isSeriesError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <ErrorState
          error={seriesError}
          message="Failed to load series"
          onRetry={refetchSeries}
        />
      </div>
    )
  }

  // Connections error (non-blocking - show toast instead)
  if (isConnectionsError) {
    toast({
      title: 'Warning',
      description: 'Failed to load connections. Some features may not work.',
      variant: 'destructive',
    })
  }

  // Series not found
  if (!series) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9F9F9]">
        <ErrorState
          error="Series not found"
          message="The series you're looking for doesn't exist or has been deleted."
          onRetry={() => navigate('/series')}
        />
      </div>
    )
  }

  // Empty series (no moves)
  const isEmpty = nodes.length === 0

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
          {/* Left Panel: Series Info */}
          <aside className="h-fit rounded-lg border bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                Series Information
              </h2>
              {isEditMode ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={updateSeriesMutation.isPending}
                    className="bg-[#0033A0] hover:bg-[#002080]"
                    size="sm"
                  >
                    {updateSeriesMutation.isPending
                      ? 'Saving...'
                      : 'Save Changes'}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleEditClick}
                    size="sm"
                  >
                    Edit Series
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    size="sm"
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>

            {isEditMode ? (
              <div className="space-y-4">
                {/* Name Input */}
                <div>
                  <label
                    htmlFor="series-name"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="series-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    maxLength={255}
                    placeholder="Enter series name"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {name.length}/255 characters
                  </p>
                </div>

                {/* Description Textarea */}
                <div>
                  <label
                    htmlFor="series-description"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="series-description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    maxLength={1000}
                    rows={4}
                    placeholder="Describe this series"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {description.length}/1000 characters
                  </p>
                </div>

                {/* Tags Section */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Tags ({seriesTags.length})
                  </label>
                  {seriesTags.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {seriesTags.map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs"
                          style={{
                            backgroundColor: tag.color
                              ? `${tag.color}20`
                              : '#f3f4f6',
                            borderColor: tag.color || '#d1d5db',
                          }}
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag.id)}
                            className="ml-1 hover:text-red-600"
                            aria-label={`Remove ${tag.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsTagDrawerOpen(true)}
                    data-testid="series-tags-action"
                    className="w-full border-[#0033A0] text-[#0033A0] hover:bg-[#0033A0]/10"
                  >
                    {seriesTags.length === 0 ? 'Add Tags' : 'Manage Tags'}
                  </Button>
                </div>

                {/* Add Moves Button */}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDrawerOpen(true)}
                    className="w-full border-[#0033A0] text-[#0033A0] hover:bg-[#0033A0]/10"
                  >
                    Add Moves to Series
                  </Button>
                </div>

                {/* Create New Move Button - Edit Mode */}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/moves/new')}
                    className="w-full"
                  >
                    Create New Move
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-700">
                    Name
                  </h3>
                  <p className="text-base text-black">{series.name}</p>
                </div>

                {/* Description */}
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-700">
                    Description
                  </h3>
                  <p className="whitespace-pre-wrap text-sm text-gray-600">
                    {series.description}
                  </p>
                </div>

                {/* Move Count */}
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-700">
                    Moves
                  </h3>
                  <p className="text-base text-black">
                    {nodes.length} {nodes.length === 1 ? 'move' : 'moves'}
                  </p>
                </div>

                {/* Connections Count */}
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-700">
                    Connections
                  </h3>
                  <p className="text-base text-black">
                    {edges.length}{' '}
                    {edges.length === 1 ? 'connection' : 'connections'}
                  </p>
                </div>

                {/* Always-visible Add Moves Button */}
                <div className="border-t pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDrawerOpen(true)}
                    className="w-full border-[#0033A0] text-[#0033A0] hover:bg-[#0033A0]/10"
                  >
                    Add Moves to Series
                  </Button>
                </div>

                {/* Tags Section */}
                <div className="border-t pt-4">
                  <div className="mb-2">
                    <h3 className="text-sm font-medium text-gray-700">
                      Tags ({seriesTags.length})
                    </h3>
                  </div>
                  {seriesTags.length === 0 ? (
                    <p className="mb-3 text-sm italic text-gray-500">
                      No tags yet
                    </p>
                  ) : (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {seriesTags.map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs"
                          style={{
                            backgroundColor: tag.color
                              ? `${tag.color}20`
                              : '#f3f4f6',
                            borderColor: tag.color || '#d1d5db',
                          }}
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag.id)}
                            className="hover:opacity-70"
                            disabled={removeTagMutation.isPending}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsTagDrawerOpen(true)}
                    data-testid="series-tags-action"
                    className="w-full border-[#0033A0] text-[#0033A0] hover:bg-[#0033A0]/10"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {seriesTags.length === 0 ? 'Add Tags' : 'Manage Tags'}
                  </Button>
                </div>

                {/* Create New Move Button - Read Mode */}
                <div className="border-t pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/moves/new')}
                    className="w-full"
                  >
                    Create New Move
                  </Button>
                </div>
              </div>
            )}
          </aside>

          {/* Right Panel: React Flow Canvas */}
          <section
            className={cn(
              'overflow-hidden rounded-lg border bg-white',
              isFullscreen &&
                'fixed inset-0 z-50 overflow-hidden rounded-none border-0'
            )}
          >
            {isEmpty ? (
              <div className="flex h-[600px] flex-col items-center justify-center px-6 text-center">
                <div className="mb-4 text-6xl">🥋</div>
                <h3 className="mb-2 text-xl font-semibold text-black">
                  No Moves Yet
                </h3>
                <p className="mb-6 max-w-md text-gray-600">
                  Add moves to this series to start building your flow diagram.
                  You'll be able to connect moves to show sequences and
                  transitions.
                </p>
                <Button
                  className="bg-[#0033A0] hover:bg-[#0033A0]/90"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Add Your First Move
                </Button>
              </div>
            ) : (
              <div
                style={{
                  height: isFullscreen ? '100vh' : '600px',
                  position: 'relative',
                }}
              >
                {/* Fullscreen Toggle Button */}
                <Button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className={cn(
                    'absolute right-2 top-2 z-10',
                    'border border-[#0033A0] bg-white text-[#0033A0] hover:bg-gray-100',
                    'shadow-md'
                  )}
                  size="sm"
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  aria-label={
                    isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
                  }
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>

                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onEdgeClick={onEdgeClick}
                  onNodeClick={onNodeClick}
                  onNodeDragStop={onNodeDragStop}
                  nodeTypes={nodeTypes}
                  nodesDraggable
                  nodesConnectable
                  edgesFocusable
                  connectionRadius={35}
                  connectionLineStyle={{ stroke: '#000000', strokeWidth: 2 }}
                  fitView
                  minZoom={0.2}
                  maxZoom={2}
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    animated: false,
                  }}
                >
                  <Background color="#0033A0" gap={16} size={1} />
                  <Controls />
                  <MiniMap
                    nodeColor="#0033A0"
                    maskColor="rgba(0, 51, 160, 0.1)"
                    style={{
                      backgroundColor: '#F9F9F9',
                    }}
                  />
                  {isEditMode && (
                    <Panel
                      position="top-center"
                      className="rounded border bg-white px-3 py-2 shadow-sm"
                    >
                      <p className="text-xs text-gray-600">
                        💡 Drag from a handle to another to create connections
                      </p>
                    </Panel>
                  )}
                </ReactFlow>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Add Moves Drawer */}
      <AddItemsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Moves to Series"
        items={allMoves.map(move => ({
          id: move.id,
          name: move.name,
          description: move.description,
          thumbnailUrl: move.thumbnailUrl,
        }))}
        currentItemIds={
          series?.seriesMoves
            ?.map((sm: SeriesMoveWithRelations) => sm.move?.id)
            .filter((id): id is string => !!id) || []
        }
        onAddItems={handleAddMoves}
        searchPlaceholder="Search moves..."
        addButtonLabel="Add Selected Moves"
        emptyMessage="No moves available"
        emptySearchMessage="No moves match your search"
      />

      {/* Add Tags Drawer */}
      <AddItemsDrawer
        isOpen={isTagDrawerOpen}
        onClose={() => setIsTagDrawerOpen(false)}
        title="Add Tags to Series"
        items={allTags.map(tag => ({
          id: tag.id,
          name: tag.name,
          description: tag.description || '',
          color: tag.color,
        }))}
        currentItemIds={seriesTags.map(tag => tag.id)}
        onAddItems={handleAddTags}
        searchPlaceholder="Search tags..."
        addButtonLabel="Add Selected Tags"
        emptyMessage="No tags available"
        emptySearchMessage="No tags match your search"
      />

      {/* Move Node Tooltip */}
      {tooltipMove && (
        <MoveNodeTooltip
          move={tooltipMove}
          position={tooltipMove.position}
          onClose={() => setTooltipMove(null)}
          onNavigate={() => {
            navigate(`/moves/${tooltipMove.id}`)
            setTooltipMove(null)
          }}
          onDelete={() => handleRemoveMove(tooltipMove.id)}
        />
      )}

      {/* Delete Series Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Series"
        description="Are you sure you want to delete this series? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="destructive"
      />

      {/* Delete Connection Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConnectionConfirm}
        onClose={() => {
          setShowDeleteConnectionConfirm(false)
          setConnectionToDelete(null)
        }}
        onConfirm={handleConfirmDeleteConnection}
        title="Delete Connection"
        description="Are you sure you want to delete this connection? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="destructive"
      />
    </div>
  )
}
